/**
 * Spa List — filtres croisés côté client sur la liste des spas.
 * Cartes rendues en Liquid (facettes en data-attributes) ; options, tri et état URL dérivés en JS.
 * Au-delà de 250 metaobjets, les pages suivantes sont chargées via la Section Rendering API.
 */

import { Component } from '@theme/component';

const FILTERS = ['continent', 'pays', 'ville', 'chaine'];

/** Valeur spéciale « toutes » : retient les cartes qui ont une valeur pour ce filtre (?chaine=toutes). */
const ALL_VALUE = 'toutes';
const FILTERS_WITH_ALL = ['chaine'];

/** Les champs de la carte balayés par la barre de recherche. */
const SEARCHABLE = ['nom', 'villeLabel', 'paysLabel', 'chaineLabel', 'continentLabel'];

/** Minuscules sans accents : « Hôtel » doit sortir sur « hotel ». */
function normalize(value) {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/** Ordre de dépendance : un filtre ne propose que des valeurs compatibles avec ceux qui le précèdent. */
const PARENTS = {
  continent: [],
  pays: ['continent'],
  ville: ['continent', 'pays'],
  chaine: ['continent', 'pays', 'ville'],
};

class SpaList extends Component {
  /** @type {HTMLElement[]} */
  #cards = [];
  /** @type {Record<string, string>} */
  #state = { continent: '', pays: '', ville: '', chaine: '' };
  #sort = 'az';
  /** Texte saisi dans la barre de recherche, tel quel puis normalisé pour la comparaison. */
  #queryRaw = '';
  #query = '';
  /** @type {Record<string, string>} */
  #search = {};
  /** Concaténation normalisée des facettes d'une carte, calculée une seule fois. */
  #haystacks = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    this.#cards = [...this.querySelectorAll('[data-spa]')];
    this.#readUrl();
    if (this.refs.query) this.refs.query.value = this.#queryRaw;
    this.#loadRemainingPages();

    this.addEventListener('click', (event) => this.#onClick(event));
    this.addEventListener('change', (event) => {
      const input = event.target.closest('[data-value]');
      if (!input) return;
      const name = input.closest('[data-filter]').dataset.filter;
      this.#state[name] = input.checked ? input.dataset.value : '';
      this.#resetChildren(name);
      this.#closeDropdowns();
      this.#render();
    });

    this.addEventListener('input', (event) => {
      const queryField = event.target.closest('[data-query]');
      if (queryField) {
        this.#queryRaw = queryField.value;
        this.#query = normalize(this.#queryRaw.trim());
        this.#render();
        return;
      }

      const field = event.target.closest('[data-search]');
      if (!field) return;
      const name = field.closest('[data-filter]').dataset.filter;
      this.#search[name] = field.value;
      this.#applySearch(name);
    });

    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.#closeDropdowns();
    });

    this.#render();
  }

  #dropdownFor(name) {
    return this.querySelector(`[data-filter="${name}"]`);
  }

  /** Masque les options qui ne contiennent pas la recherche saisie dans ce dropdown. */
  #allLabel(name) {
    return this.dataset[`labelAll${name.charAt(0).toUpperCase()}${name.slice(1)}`] || 'Toutes';
  }

  #applySearch(name) {
    const dropdown = this.#dropdownFor(name);
    const query = (this.#search[name] || '').trim().toLowerCase();
    if (!dropdown) return;

    for (const item of dropdown.querySelectorAll('[data-options] li')) {
      const text = item.textContent.toLowerCase();
      item.hidden = query !== '' && !text.includes(query);
    }
  }

  #closeDropdowns() {
    for (const panel of this.querySelectorAll('[data-dropdown-panel]')) panel.classList.remove('is-open');
  }

  #onClick(event) {
    const toggle = event.target.closest('[data-dropdown-toggle]');
    if (toggle) {
      const panel = toggle.parentElement.querySelector('[data-dropdown-panel]');
      const wasOpen = panel?.classList.contains('is-open');
      this.#closeDropdowns();
      if (!wasOpen) panel?.classList.add('is-open');
      return;
    }

    if (event.target.closest('[data-dropdown-close]')) {
      this.#closeDropdowns();
      return;
    }

    if (event.target.closest('[data-query-clear]')) {
      this.#clearQuery();
      return;
    }

    const sortButton = event.target.closest('[data-sort-value]');
    if (sortButton) {
      this.#sort = sortButton.dataset.sortValue;
      this.#closeDropdowns();
      this.#render();
      return;
    }

    const chip = event.target.closest('[data-remove]');
    if (chip) {
      event.preventDefault();
      if (chip.dataset.remove === 'all') {
        for (const name of FILTERS) this.#state[name] = '';
        this.#clearQuery({ render: false });
      } else if (chip.dataset.remove === 'query') {
        this.#clearQuery({ render: false });
      } else {
        this.#state[chip.dataset.remove] = '';
        this.#resetChildren(chip.dataset.remove);
      }
      this.#render();
    }
  }

  #clearQuery({ render = true } = {}) {
    this.#queryRaw = '';
    this.#query = '';
    if (this.refs.query) this.refs.query.value = '';
    if (render) this.#render();
  }

  #haystack(card) {
    let value = this.#haystacks.get(card);
    if (value === undefined) {
      value = normalize(SEARCHABLE.map((key) => card.dataset[key] || '').join(' '));
      this.#haystacks.set(card, value);
    }
    return value;
  }

  /** Changer un filtre parent invalide les enfants : on les remet à zéro. */
  #resetChildren(changed) {
    for (const name of FILTERS) {
      if (PARENTS[name].includes(changed)) this.#state[name] = '';
    }
  }

  /** Cartes retenues, en ignorant éventuellement un filtre (pour construire ses propres options). */
  #matching(ignore) {
    return this.#cards.filter((card) => {
      if (this.#query && !this.#haystack(card).includes(this.#query)) return false;

      return FILTERS.every((name) => {
        if (name === ignore) return true;
        const value = this.#state[name];
        if (value === ALL_VALUE && FILTERS_WITH_ALL.includes(name)) return !!card.dataset[name];
        return !value || card.dataset[name] === value;
      });
    });
  }

  #syncOptions() {
    for (const name of FILTERS) {
      const dropdown = this.#dropdownFor(name);
      const list = dropdown?.querySelector('[data-options]');
      if (!list) continue;

      const counts = new Map();
      const labels = new Map();
      for (const card of this.#matching(name)) {
        const key = card.dataset[name];
        if (!key) continue;
        counts.set(key, (counts.get(key) || 0) + 1);
        if (!labels.has(key)) labels.set(key, card.dataset[`${name}Label`] || key);
      }

      const options = [...counts.keys()].sort((a, b) => labels.get(a).localeCompare(labels.get(b), 'fr'));
      // Option « Toutes les chaînes » en tête, dès qu'il y a au moins deux valeurs à regrouper.
      if (FILTERS_WITH_ALL.includes(name) && options.length > 1) {
        let total = 0;
        for (const count of counts.values()) total += count;
        options.unshift(ALL_VALUE);
        counts.set(ALL_VALUE, total);
        labels.set(ALL_VALUE, this.#allLabel(name));
      }
      list.replaceChildren();

      for (const value of options) {
        const item = document.createElement('li');
        const label = document.createElement('label');
        label.className = 'tw:flex tw:items-center tw:justify-between tw:px-5 tw:py-1.5 tw:cursor-pointer tw:text-base';
        label.innerHTML = `<span>${labels.get(value)} (${counts.get(value)})</span>`;

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.dataset.value = value;
        input.checked = this.#state[name] === value;
        input.className =
          'filter-checkbox tw:appearance-none tw:w-6 tw:h-6 tw:bg-background tw:border tw:border-border tw:rounded-[4px] tw:cursor-pointer tw:relative tw:shrink-0';
        label.append(input);
        item.append(label);
        list.append(item);
      }

      this.#applySearch(name);
    }

    for (const button of this.querySelectorAll('[data-sort-value]')) {
      button.classList.toggle('tw:font-bold', button.dataset.sortValue === this.#sort);
    }
  }

  #render() {
    this.#syncOptions();

    const visible = this.#matching();
    const shown = new Set(visible);
    for (const card of this.#cards) card.hidden = !shown.has(card);

    const direction = this.#sort === 'za' ? -1 : 1;
    const grid = this.refs.grid;
    if (grid) {
      const sorted = [...visible].sort(
        (a, b) => direction * a.dataset.nom.localeCompare(b.dataset.nom, 'fr')
      );
      for (const card of sorted) grid.append(card);
    }

    if (this.refs.count) {
      const plural = this.dataset.labelResults || 'résultats';
      const singular = this.dataset.labelResult || 'résultat';
      this.refs.count.textContent = `${visible.length} ${visible.length > 1 ? plural : singular}`;
    }
    if (this.refs.empty) this.refs.empty.hidden = visible.length > 0;
    if (this.refs.queryClear) this.refs.queryClear.hidden = this.#queryRaw === '';

    this.#renderChips();
    this.#writeUrl();
  }

  #renderChips() {
    const chips = this.refs.chips;
    if (!chips) return;

    const active = FILTERS.filter((name) => this.#state[name]);
    const hasQuery = this.#query !== '';
    chips.hidden = active.length === 0 && !hasQuery;
    chips.replaceChildren();

    const cross =
      '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" class="tw:shrink-0"><path d="M13.5 4.5L4.5 13.5" stroke="#6E7D91" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"/><path d="M4.5 4.5L13.5 13.5" stroke="#6E7D91" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    if (hasQuery) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.dataset.remove = 'query';
      chip.className =
        'tw:flex tw:items-center tw:gap-2 tw:text-sm tw:border tw:text-muted tw:border-background-alt tw:bg-transparent tw:rounded-[2px] tw:px-4 tw:py-2 tw:cursor-pointer tw:transition-colors tw:hover:bg-surface';
      chip.textContent = this.#queryRaw.trim();
      chip.insertAdjacentHTML('beforeend', cross);
      chips.append(chip);
    }

    for (const name of active) {
      const dropdown = this.#dropdownFor(name);
      const filterLabel = dropdown?.querySelector('[data-dropdown-toggle]')?.textContent.trim() || name;
      const card = this.#cards.find((item) => item.dataset[name] === this.#state[name]);
      const valueLabel =
        this.#state[name] === ALL_VALUE && FILTERS_WITH_ALL.includes(name)
          ? this.#allLabel(name)
          : card?.dataset[`${name}Label`] || this.#state[name];

      const chip = document.createElement('button');
      chip.type = 'button';
      chip.dataset.remove = name;
      chip.className =
        'tw:flex tw:items-center tw:gap-2 tw:text-sm tw:border tw:text-muted tw:border-background-alt tw:bg-transparent tw:rounded-[2px] tw:px-4 tw:py-2 tw:cursor-pointer tw:transition-colors tw:hover:bg-surface';
      chip.innerHTML = `${filterLabel}: ${valueLabel}${cross}`;
      chips.append(chip);
    }

    if (active.length > 0 || hasQuery) {
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.dataset.remove = 'all';
      clear.className = 'tw:bg-transparent tw:border-0 tw:text-xl tw:whitespace-nowrap tw:ml-1 tw:cursor-pointer';
      clear.innerHTML = '<span class="cta-link__text">Tout effacer</span>';
      chips.append(clear);
    }
  }

  #readUrl() {
    const params = new URLSearchParams(window.location.search);
    for (const name of FILTERS) this.#state[name] = params.get(name) || '';
    this.#queryRaw = params.get('recherche') || '';
    this.#query = normalize(this.#queryRaw.trim());
    this.#sort = params.get('tri') === 'za' ? 'za' : 'az';
  }

  #writeUrl() {
    // Liste quittée entre-temps (navigation sans rechargement) : ne pas toucher l'URL de la nouvelle page.
    if (!this.isConnected) return;
    const params = new URLSearchParams(window.location.search);
    for (const name of FILTERS) {
      if (this.#state[name]) params.set(name, this.#state[name]);
      else params.delete(name);
    }
    const typed = this.#queryRaw.trim();
    if (typed) params.set('recherche', typed);
    else params.delete('recherche');

    if (this.#sort === 'za') params.set('tri', 'za');
    else params.delete('tri');

    const query = params.toString();
    window.history.replaceState({}, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
  }

  // Suit le lien « page suivante » de Shopify : pour les metaobjects, `?page=N` seul est ignoré
  // (il faut aussi le curseur phcursor), ce qui renvoyait la page 1 en boucle et dupliquait les cartes.
  async #loadRemainingPages() {
    let next = this.refs.pagination?.dataset.next;
    if (!next) return;

    const sectionId = this.closest('[data-section-id]')?.dataset.sectionId;
    if (!sectionId) return;

    const seen = new Set(this.#cards.map((card) => card.getAttribute('href')));
    const visited = new Set();

    while (next && !visited.has(next) && visited.size < 20) {
      visited.add(next);
      try {
        const url = new URL(next, window.location.origin);
        url.searchParams.set('section_id', sectionId);
        const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) break;

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const extra = [...doc.querySelectorAll('[data-spa]')].filter((card) => !seen.has(card.getAttribute('href')));
        extra.forEach((card) => seen.add(card.getAttribute('href')));
        this.refs.grid?.append(...extra);
        this.#cards.push(...extra);

        next = doc.querySelector('[ref="pagination"]')?.dataset.next;
      } catch {
        // Une page manquante ne doit pas casser la liste déjà affichée.
        break;
      }
    }

    this.#render();
  }
}

if (!customElements.get('spa-list')) customElements.define('spa-list', SpaList);
